import {
  faCartCircleCheck,
  faCartShopping,
  faCircleUser,
  faGridHorizontal,
  faListCheck,
} from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, css, styled } from '@mui/material';
import Tab, { TabProps } from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import samsonLogoUrl from 'src/assets/logo-default.svg';
import { FlexContainer } from 'src/components/FlexContainer';
import { isFeatureSupported } from 'src/Feature';
import { usePermissions } from 'src/hooks/usePermissions';
import useShoppingCartQuery from 'src/providers/ShoppingCart/useShoppingCartQuery';
import { useTenant } from 'src/providers/Tenant';
import { useUser } from 'src/providers/User';
import { CurrencySwitcher } from '../CurrencySwitcher/CurrencySwitcher';
import { LogoLinkElement, StyledClientLogo } from '../styles';
import { TenantSwitcher } from '../TenantSwitcher';

export function DesktopMenu() {
  const { logoUrl } = useUser();
  const { t } = useTranslation('navigation');
  const { enabledFeatures, name } = useTenant();

  const hasPermissionsToOperateOnAllOfTenantsOrders = usePermissions(
    ['list', 'order.history'],
    ['view', 'order.history'],
    ['update', 'order.history']
  );

  const { data: cart } = useShoppingCartQuery();
  const hasPermissionsToViewOwnsOrders = usePermissions(['list', 'order'], ['view', 'order'], ['email', 'order']);

  /* istanbul ignore next */
  const itemsInCart = cart.reduce((total, { quantity }) => total + quantity, 0);

  const { pathname } = useLocation();
  const activeTab =
    ['/my-orders', '/received-orders', '/cart', '/product-library', '/my-account'].find((tab) =>
      pathname.startsWith(tab)
    ) ?? '/product-library';

  const displayReceivedOrders =
    hasPermissionsToOperateOnAllOfTenantsOrders && isFeatureSupported('shopping-cart', enabledFeatures);
  const displayMyOrders =
    !hasPermissionsToOperateOnAllOfTenantsOrders &&
    hasPermissionsToViewOwnsOrders &&
    /* istanbul ignore next */
    isFeatureSupported('shopping-cart', enabledFeatures);

  const displayShoppingCart = isFeatureSupported('shopping-cart', enabledFeatures);

  const tabItemLine = {
    ':after': {
      content: "''",
      position: 'absolute',
      right: '0',
      width: '1px',
      height: '27px',
      backgroundColor: '#616161',
    },
  };

  return (
    <DesktopMenuContainer>
      <LogoLinkElement to="/" data-testid="tenant-logo" title={t('libraryLinkDescription')}>
        <StyledClientLogo alt={t('logoDescription', { account: name })} src={logoUrl ?? samsonLogoUrl} />
      </LogoLinkElement>
      <FlexContainer sx={{ alignItems: 'center', mx: 2 }}>
        <TenantSwitcher />
      </FlexContainer>
      <StyledTabs
        value={activeTab}
        TabIndicatorProps={{
          sx: { background: (theme) => theme.palette.secondary.main },
        }}
      >
        <StyledTab
          sx={{ ml: 'auto', ...tabItemLine }}
          icon={<FontAwesomeIcon icon={faGridHorizontal} className="fa-xl" />}
          label={t('library')}
          value="/product-library"
          to="/product-library"
          component={Link}
        />

        <StyledTab
          sx={tabItemLine}
          component={CurrencySwitcher}
          // Tab requires a path but we are not wanting to move to another page so leave it empty
          to=""
        />

        {displayReceivedOrders ? (
          <StyledTab
            sx={tabItemLine}
            icon={<FontAwesomeIcon icon={faListCheck} className="fa-xl" />}
            label={t('receivedOrders')}
            value="/received-orders"
            to="/received-orders"
            component={Link}
          />
        ) : null}

        {displayMyOrders ? (
          /* istanbul ignore next */
          <StyledTab
            sx={tabItemLine}
            icon={<FontAwesomeIcon icon={faCartCircleCheck} className="fa-xl" />}
            label={t('myOrders')}
            value="/my-orders"
            to="/my-orders"
            component={Link}
          />
        ) : null}

        {displayShoppingCart ? (
          <StyledTab
            sx={tabItemLine}
            icon={<FontAwesomeIcon icon={faCartShopping} className="fa-xl" />}
            label={
              <div>
                {t('shoppingCart')} <sup>({itemsInCart})</sup>
              </div>
            }
            value="/cart"
            to="/cart"
            component={Link}
            data-testid="nav-shopping-cart"
          />
        ) : null}

        <StyledTab
          icon={<FontAwesomeIcon icon={faCircleUser} className="fa-xl" />}
          label={t('myAccount')}
          value="/my-account"
          to="/my-account"
          component={Link}
          data-testid="my-account-link"
        />
      </StyledTabs>
    </DesktopMenuContainer>
  );
}

const StyledTabs = styled(Tabs)`
  ${({ theme }) => css`
    .MuiTab-root,
    .MuiTab-root.Mui-selected,
    .MuiTypography-root .MuiTypography-button {
      color: ${theme.palette.brand.contrastText};
      text-transform: capitalize;
      font-size: 0.875rem;
    }
    width: 100%;
    margin-right: ${theme.spacing(2)}px;
    .MuiTab-iconWrapper {
      font-size: 1.8rem;
    }
  `}
`;

const StyledTab = styled(Tab)<TabProps & LinkProps>`
  min-height: 4.5rem; // SVT-2029 overriding material's 72px with rem's to enable scaling.
  border-bottom: 2px solid transparent;
  transition: border-bottom 0.25s ease-in-out;
  &:hover {
    transition: border-bottom 0.25s ease-in-out;
    border-bottom: 2px solid ${({ theme }) => theme.palette.secondary.main};
  }
`;

const DesktopMenuContainer = styled(Box)`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;
